import { getAppMetadata } from '@/plan/metadata/metadata'
import { Color } from '@deck.gl/core'
import {
  getPOIsColors,
  getPOIsDefaultColor,
} from '@/plan/metadata/metadata.helper'
import {
  getCachedPOIsBufferDistance,
  getCachedPOIsFilters,
} from './pois-filter-cache'

interface PoiTileset {
  properties: {
    b: number // Brand
    sc: number // Sub Category
    tc: number // Top Category
    normalized_location_name: string
    location_name: string
    cts: string // Category Tags in format: "#1#4#7#"
    c: number // Country
  }
}

function getPOIsRampColorsAsRGB(): Color[] {
  const { ramp } = getPOIsColors(getAppMetadata())
  const rampAsRGB = ramp.map((color) => hexToRGBA(color))
  return rampAsRGB
}

export const POIsDefaultColor = hexToRGBA(getPOIsDefaultColor(getAppMetadata()))
export const POIsOthersColor = hexToRGBA(getPOIsColors(getAppMetadata()).others)
export const POIsRampColor = getPOIsRampColorsAsRGB()

export function filterPois(poi: PoiTileset): number {
  // console.log('🟪 Filtering POIs on tileset')
  // Use cached filter values for high performance
  const cachedFilters = getCachedPOIsFilters()
  const properties = poi.properties
  const { subCategories, topCategories, brands, locations, tags, countries } =
    cachedFilters

  // Check countries filter
  if (countries.size > 0 && !countries.has(properties.c)) {
    return 0
  }

  // Check brands filter
  if (brands.has(properties.b)) {
    return 1
  }

  // Check tags filter using optimized string includes
  // properties.cts format: "#1#4#7#" (tag IDs separated by #)
  if (properties.cts && tags.size > 0) {
    for (const tag of tags) {
      if (properties.cts.includes(`#${tag}#`)) {
        return 1
      }
    }
  }

  // Check categories filter
  if (properties.sc === undefined || properties.sc === null) {
    if (topCategories.has(properties.tc)) {
      return 1
    }
  } else {
    if (subCategories.has(properties.sc)) {
      return 1
    }
  }

  // Check locations filter
  if (locations.has(properties.location_name)) {
    return 1
  }

  return 0
}

export function getBufferRangeDistanceForPOIs(excludeBuffer = false): number {
  const buffersDistances = getCachedPOIsBufferDistance()
  return excludeBuffer ? buffersDistances[0] : buffersDistances[1]
}

export function getBufferRangeDistanceAccessor(
  excludeBuffer = false,
): () => number {
  return () => getBufferRangeDistanceForPOIs(excludeBuffer)
}

export function hexToRGBA(hex: string): Color {
  const bigint = parseInt(hex.slice(1), 16)
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255, 255]
}

export function rgbaToHex(rgba: Color): string {
  let r = rgba[0].toString(16)
  let g = rgba[1].toString(16)
  let b = rgba[2].toString(16)
  let a = (rgba[3] ?? 255).toString(16)

  if (r.length === 1) {
    r = '0' + r
  }
  if (g.length === 1) {
    g = '0' + g
  }
  if (b.length === 1) {
    b = '0' + b
  }
  if (a.length === 1) {
    a = '0' + a
  }

  return '#' + r + g + b + a
}

interface LayerPOI {
  brands?: string
  category_tags?: string
  location_name?: string
  top_category?: string
  sub_category?: string
  id?: string
}

export function getColorFromRamp(
  properties: LayerPOI | null,
  poisColors: Record<string, Color>,
): Color {
  if (!properties) {
    return POIsDefaultColor
  }
  if (properties.brands && poisColors[properties.brands]) {
    return poisColors[properties.brands]
  }
  if (properties.category_tags && poisColors[properties.category_tags]) {
    return poisColors[properties.category_tags]
  }
  if (properties.location_name && poisColors[properties.location_name]) {
    return poisColors[properties.location_name]
  }
  if (properties.top_category && poisColors[properties.top_category]) {
    return poisColors[properties.top_category]
  }
  if (properties.sub_category && poisColors[properties.sub_category]) {
    return poisColors[properties.sub_category]
  }
  // custom POIS
  if (properties.id && poisColors[properties.id]) {
    return poisColors[properties.id]
  }
  return POIsDefaultColor
}
